import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

const Container = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    min-h-screen
    bg-gray-50
  `};
`;

const Sidebar = styled.nav`
  ${tw`
    w-full
    md:w-64
    bg-white
    border-b
    md:border-b-0
    md:border-r
    border-gray-200
    p-6
    sticky
    top-0
    md:h-screen
    overflow-y-auto
    z-10
  `};
`;

const NavItem = styled.button<{ active: boolean }>`
  ${tw`
    w-full
    text-left
    px-4
    py-2
    rounded-lg
    text-sm
    font-medium
    mb-2
    transition-all max-w-full min-w-0 box-border`};
  ${({ active }) =>
    active ? tw`bg-blue-50 text-blue-600` : tw`text-gray-600 hover:bg-gray-50 hover:text-gray-900`};
`;

const MainContent = styled.main`
  ${tw`
    flex-1
    p-6
    md:p-12
    max-w-4xl
  `};
`;

const Header = styled.div`
  ${tw`mb-12`};
`;

const Title = styled.h1`
  ${tw`
    text-4xl
    font-extrabold
    text-gray-900
    mb-4
  `};
`;

const Subtitle = styled.p`
  ${tw`
    text-lg
    text-gray-600
  `};
`;

const Section = styled.div`
  ${tw`mb-16`};
`;

const SectionTitle = styled.h2`
  ${tw`
    text-2xl
    font-bold
    text-gray-800
    mb-8
    pb-2
    border-b-2
    border-blue-500
    inline-block
  `};
`;

const ApiCard = styled.div`
  ${tw`
    bg-white
    rounded-2xl
    shadow-sm
    border
    border-gray-200
    p-6
    md:p-8
    mb-8
  `};
`;

const ApiHeader = styled.div`
  ${tw`
    flex
    flex-wrap
    items-center
    justify-between
    mb-4
    gap-2
  `};
`;

const ApiName = styled.h3`
  ${tw`
    text-lg
    font-bold
    text-gray-900
    font-mono
  `};
`;

const MethodBadge = styled.span<{ type: 'query' | 'mutation' }>`
  ${tw`
    px-3
    py-0.5
    rounded-full
    text-[10px]
    font-bold
    uppercase
  `};
  ${({ type }) =>
    type === 'query' ? tw`bg-green-100 text-green-700` : tw`bg-orange-100 text-orange-700`};
`;

const Description = styled.p`
  ${tw`
    text-gray-700
    mb-6
    text-sm
    leading-relaxed
  `};
`;

const CodeBlock = styled.pre`
  ${tw`
    bg-gray-900
    text-gray-100
    p-4
    rounded-xl
    text-xs
    overflow-x-auto
    font-mono
    mt-4
  `};
`;

const Label = styled.div`
  ${tw`
    text-[10px]
    font-bold
    text-gray-400
    uppercase
    tracking-wider
    mb-2
  `};
`;

const Grid = styled.div`
  ${tw`
    grid
    grid-cols-1
    lg:grid-cols-2
    gap-6
  `};
`;

const Box = styled.div`
  ${tw`flex flex-col`};
`;

const Footer = styled.div`
  ${tw`
    mt-6
    pt-4
    border-t
    border-gray-100
    text-xs
    text-gray-500
  `};
`;

const Meta = styled.div`
  ${tw`
    mt-4
    grid
    grid-cols-1
    sm:grid-cols-3
    gap-3
    text-xs
    text-gray-600
  `};
`;

const MetaRow = styled.div`
  ${tw`flex flex-col`};
`;

const MetaLabel = styled.span`
  ${tw`text-[10px] font-bold uppercase tracking-wider text-gray-400`};
`;

const MetaValue = styled.span`
  ${tw`text-sm text-gray-700`};
`;

const sections = [
  { id: 'overview', title: 'System Overview' },
  { id: 'backend-diff', title: 'Backend vs Main' },
  { id: 'pre-booking', title: 'Pre-Booking' },
  { id: 'core-booking', title: 'Core Booking' },
  { id: 'stripe', title: 'Stripe & Payments' },
  { id: 'deposit', title: 'Security Deposit' },
  { id: 'modification', title: 'Trip Modifications' },
  { id: 'operations', title: 'Ops & Swapping' },
  { id: 'cancellation', title: 'Care & Cancellation' },
];

const PaymentApiStoryBoard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const scrollTo = (id: string) => {
    setActiveTab(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Container>
      <Sidebar>
        <Label style={{ paddingLeft: '1rem', marginBottom: '1rem' }}>Lifecycle Stages</Label>
        {sections.map(s => (
          <NavItem key={s.id} active={activeTab === s.id} onClick={() => scrollTo(s.id)}>
            {s.title}
          </NavItem>
        ))}
      </Sidebar>

      <MainContent>
        <Header>
          <Title>Autopilotee API Storyboard</Title>
          <Subtitle>
            The complete technical reference for Booking and Payment orchestration.
          </Subtitle>
        </Header>

        {/* Overview */}
        <Section id="overview">
          <SectionTitle>System Overview</SectionTitle>
          <ApiCard>
            <Description>
              The Autopilotee system uses a **GraphQL** backend built with NestJS. All financial
              transactions are handled via **Stripe**. The booking lifecycle follows a state
              machine:
              <br />
              <br />
              <code>PENDING</code> → <code>BOOKED</code> → <code>STARTED</code> →{' '}
              <code>COMPLETED</code>
              <br />
              <br />
              Additionally, we implement a <strong>Deposit First</strong> flow to ensure trust
              between hosts and guests.
            </Description>
          </ApiCard>
        </Section>

        {/* Backend vs Main */}
        <Section id="backend-diff">
          <SectionTitle>Backend vs Main (Current Branch)</SectionTitle>
          <ApiCard>
            <Description>
              Diff summary between <strong>bettersolution-payment-deposit-refund</strong> and{' '}
              <strong>main</strong>. Focused on GraphQL API additions and signature changes.
            </Description>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>Added Mutations</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              New booking/deposit-related mutations introduced on this branch.
            </Description>
            <CodeBlock>{`confirmCarConditionClear(bookingId: string) -> Booking
reportCarDamage(bookingId: string, damageDescription: string, claimAmount: number) -> Booking
resolveDepositDispute(bookingId: string, approvedChargeAmount: number, resolution: string) -> Booking
chargePostTripFee(input: ChargePostTripFeeInput) -> ChargePostTripFeeResult`}</CodeBlock>
            <CodeBlock>{`ChargePostTripFeeInput {
  bookingId: string
  chargeType: InvoiceType
  amount: number
  description: string
  evidenceFiles?: EvidenceFilesInput
}`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>Changed Mutations</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Signature changes compared to main.</Description>
            <CodeBlock>{`holdSecurityDeposit(bookingId: string, reason?: string) -> Booking
releaseSecurityDeposit(bookingId: string, releaseAmount: number, reason?: string) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>Added Booking Fields</ApiName>
              <MethodBadge type="query">Query</MethodBadge>
            </ApiHeader>
            <Description>New fields available on the Booking type.</Description>
            <CodeBlock>{`securityDepositHoldReason?: string
securityDepositReleaseReason?: string
hostConfirmedCarClear?: boolean
hostConfirmedCarClearAt?: Date
damageReportedAt?: Date
damageReportDescription?: string
damageReportAmount?: number
actualDamageChargeAmount?: number
disputeResolvedAt?: Date
disputeResolution?: string`}</CodeBlock>
          </ApiCard>
        </Section>

        {/* Pre-Booking */}
        <Section id="pre-booking">
          <SectionTitle>Pre-Booking Phase</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>calculateCarPricingWithoutTax</ApiName>
              <MethodBadge type="query">Query</MethodBadge>
            </ApiHeader>
            <Description>Calculates costs and fees before a booking is even intended.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-01</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-03</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>ViewCarPage, CheckoutPage</MetaValue>
              </MetaRow>
            </Meta>
            <Grid>
              <Box>
                <Label>Inputs</Label>
                <CodeBlock>{`{
  carId, startAt, endAt,
  pickupLocationId,
  returnLocationId,
  driverAge
}`}</CodeBlock>
              </Box>
              <Box>
                <Label>Logic</Label>
                <Description>
                  Estimates: Base Price, Trip Fee (Platform fee), Youngster Fee.
                </Description>
              </Box>
            </Grid>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>checkCarAvailability</ApiName>
              <MethodBadge type="query">Query</MethodBadge>
            </ApiHeader>
            <Description>Verify if a car is available for the given timeframe.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-26</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-03</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage, SearchResultPage</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`checkCarAvailability(carId, startAt, endAt) -> { isAvailable: boolean }`}</CodeBlock>
          </ApiCard>
        </Section>

        {/* Core Booking */}
        <Section id="core-booking">
          <SectionTitle>Core Booking Phase</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>createBookingIntent</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Atomically creates a PENDING booking record.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-26</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`createBookingIntent(CreateBookingInput) -> Booking`}</CodeBlock>
            <Footer>This blocks the car calendar temporarily (soft lock).</Footer>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>confirmBooking</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Finalizes the booking after payment success.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-26</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`confirmBooking(ConfirmBookingInput) -> Booking`}</CodeBlock>
            <Footer>Transitions status to BOOKED and triggers notifications.</Footer>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>myBookings / bookingsForMyListings</ApiName>
              <MethodBadge type="query">Query</MethodBadge>
            </ApiHeader>
            <Description>
              Retrieve list of bookings for Guest (myBookings) or Host (listings).
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-26</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>MyBookingsPage / BookedMyCarsPage</MetaValue>
              </MetaRow>
            </Meta>
          </ApiCard>
        </Section>

        {/* Stripe & Payments */}
        <Section id="stripe">
          <SectionTitle>Stripe & Payments</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>createSetupIntentForBooking</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Creates a SetupIntent to authorize the security deposit.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-31</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage (Deposit First)</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`createSetupIntentForBooking(bookingId) -> clientSecret`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>createRentalPaymentIntent</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Creates a PaymentIntent for the actual rental price.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-31</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage (Deposit First)</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`createRentalPaymentIntent(bookingId, paymentMethodId) -> clientSecret`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>getPaymentMethodFromSetupIntent</ApiName>
              <MethodBadge type="query">Query</MethodBadge>
            </ApiHeader>
            <Description>
              Retrieves the attached card details from a completed SetupIntent.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-31</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
          </ApiCard>
        </Section>

        {/* Security Deposit */}
        <Section id="deposit">
          <SectionTitle>Security Deposit Management</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>holdSecurityDeposit</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Admin/System triggered to capture the authorized deposit amount from the Stripe
              SetupIntent.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-11-22</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`holdSecurityDeposit(bookingId: string, reason?: string) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>releaseSecurityDeposit</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Releases a specific amount of the held security deposit back to the guest.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-11-22</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`releaseSecurityDeposit(
  bookingId: string,
  releaseAmount: number,
  reason?: string
) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>reportCarDamage</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Allows the host to report damage and claim a portion of the security deposit.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-11-22</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`reportCarDamage(
  bookingId: string,
  damageDescription: string,
  claimAmount: number
) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>resolveDepositDispute</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Admin utility to resolve disputes over security deposit claims.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-11-22</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`resolveDepositDispute(
  bookingId: string,
  approvedChargeAmount: number,
  resolution: string
) -> Booking`}</CodeBlock>
          </ApiCard>
        </Section>

        {/* Trip Modification */}
        <Section id="modification">
          <SectionTitle>Trip Modifications</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>calculateTripExtension</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Calculates the additional costs for extending a trip before actual charging.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-08-20</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`calculateTripExtension(input: CalculateTripExtensionInput) -> TripExtensionResponse`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>confirmTripExtension</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Finalizes the trip extension by charging the guest's saved card.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-08-20</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`confirmTripExtension(input: ConfirmTripExtensionInput) -> TripExtensionResponse`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>addDriverToBooking</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Adds an additional driver's verification details to a booking.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-07-15</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`addDriverToBooking(input: AddDriverInput) -> Booking`}</CodeBlock>
          </ApiCard>
        </Section>

        {/* Ops & Swapping */}
        <Section id="operations">
          <SectionTitle>Operations & Swapping</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>requestCarSwap</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Triggered when a vehicle needs to be swapped for another one in the host's fleet.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-12-29</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-03</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CarSwapModal</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`requestCarSwap(input: CarSwapRequestInput) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>approveCarSwap / rejectCarSwap</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Guest or Admin response to a car swap request.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-12-29</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-03</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`approveCarSwap(input: CarSwapResponseInput) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>uploadTripPhoto</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Upload check-in and check-out photos of the vehicle.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-06-10</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`uploadTripPhoto(input: UploadTripPhotoInput) -> Booking`}</CodeBlock>
          </ApiCard>
        </Section>

        {/* Care & Cancellation */}
        <Section id="cancellation">
          <SectionTitle>Care & Cancellation</SectionTitle>
          <ApiCard>
            <ApiHeader>
              <ApiName>cancelBooking</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Terminates the booking and prepares refund metadata based on the cancellation policy.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-26</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>CheckoutPage, BookingDetails</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`cancelBooking(
  bookingId: string,
  bookingStatus: BookingStatus,
  balance: number,
  isCancellationPenaltyApplied: boolean,
  invoiceId: string,
  cancellationPenalty: number,
  cancellationReason?: CancellationReason
) -> Booking`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>executeRefund</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>Triggers actual money movement via Stripe for a cancellation.</Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2024-12-31</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`executeRefund(
  bookingId: string,
  invoiceId: string,
  amount: number,
  reason?: string
) -> RefundResponse`}</CodeBlock>
          </ApiCard>

          <ApiCard>
            <ApiHeader>
              <ApiName>chargePostTripFee</ApiName>
              <MethodBadge type="mutation">Mutation</MethodBadge>
            </ApiHeader>
            <Description>
              Charges for post-trip incidents such as fuel, cleaning, or late return fees.
            </Description>
            <Meta>
              <MetaRow>
                <MetaLabel>Created</MetaLabel>
                <MetaValue>2025-03-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Updated</MetaLabel>
                <MetaValue>2026-01-05</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Usage</MetaLabel>
                <MetaValue>Not used in web</MetaValue>
              </MetaRow>
            </Meta>
            <CodeBlock>{`chargePostTripFee(input: ChargePostTripFeeInput) -> ChargePostTripFeeResult`}</CodeBlock>
          </ApiCard>
        </Section>
      </MainContent>
    </Container>
  );
};

export default PaymentApiStoryBoard;
